import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Modal,
} from "@mui/material";

import DateInput from "../../SharedComponents/DateInput";
import { Box, Typography } from "@mui/material";
import DataTable from "react-data-table-component";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SignatureCanvas from "react-signature-canvas";
import {
  createMedicalCertificate,
  getMedicalCertificates,
  updateMedicalCertificate,
  deleteMedicalCertificate,
} from "../../../apis/medicalCertificate";
import { toastify } from "../../../helperFunctions/toastify";
import { PDFViewer } from "@react-pdf/renderer";
import MedicalCertificatePDF from "./MedicalCertificatePDF";
import { getSetting } from "../../../apis/generalSetting";

const MedicalCertificate = ({ patient, userData, isDoctor }) => {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const fetchCertificates = async () => {
    try {
      const res = await getMedicalCertificates(patient?._id);
      if (res?.status === "Success") {
        setData(res.data || []);
      } else {
        toastify(
          res?.message || "Failed to fetch medical certificates",
          "error"
        );
      }
    } catch (error) {
      toastify("Something went wrong fetching certificates", "error");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // ======== FORM STATE ======== //
  const sigRef = useRef(null);
  const [mcType, setMcType] = useState("medical");
  const [issueDate, setIssueDate] = useState(dayjs());
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [doctor, setDoctor] = useState(
    userData ? `${userData.first_name} ${userData.last_name}` : ""
  );
  const [referenceNo, setReferenceNo] = useState("");
  const [description, setDescription] = useState("");
  const [accompaniedBy, setAccompaniedBy] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [setting, setSetting] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);

  useEffect(() => {
    getSetting().then((res) => {
      if (res.status !== "Fail") {
        setSetting(res);
      }
    });
  }, []);

  useEffect(() => {
    if (userData) {
      setDoctor(`${userData.first_name} ${userData.last_name}`);
    }
  }, [userData]);

  // Edit
  const handleEdit = (row) => {
    // Prefill form
    setIssueDate(dayjs(row.issueDate));
    setFromDate(dayjs(row.fromDate));
    setToDate(dayjs(row.toDate));
    setMcType(row.mcType);
    setDoctor(row.doctor);
    setReferenceNo(row.referenceNo);
    setDescription(row.description);
    setAccompaniedBy(row.accompaniedBy);
    if (sigRef.current) sigRef.current.fromDataURL(row.signature);

    setOpen(true);
    setSelectedCertificate(row); // keep track of editing
  };

  // Save Edited Certificate
  const handleSave = async () => {
    try {
      if (!issueDate || !fromDate || !toDate || !description || !doctor) {
        toastify("Please fill all required fields!", "warn");
        return;
      }
      if (!sigRef.current || sigRef.current.isEmpty()) {
        toastify("Please provide a doctor signature!", "warn");
        return;
      }

      setIsLoading(true);

      const payload = {
        issueDate: issueDate.toISOString(),
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        mcType,
        doctor,
        referenceNo,
        description,
        accompaniedBy: mcType === "attendance" ? accompaniedBy : "",
        patient: patient, // full patient object
        signature: sigRef.current.toDataURL(),
      };

      let res;
      if (selectedCertificate) {
        // update
        res = await updateMedicalCertificate(selectedCertificate._id, payload);
      } else {
        // create
        res = await createMedicalCertificate(payload);
      }

      if (res?.success || res?.status === "Success") {
        toastify(res?.message || "Medical certificate saved!", "success");
        setOpen(false);
        fetchCertificates();
        clearForm();
        setSelectedCertificate(null);
      } else {
        toastify(res?.message || "Failed to save certificate", "error");
      }
    } catch (error) {
      console.error(error);
      toastify("An error occurred while saving certificate", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Open modal instead of window.confirm
  const confirmDelete = (row) => {
    setCertificateToDelete(row);
    setDeleteModalOpen(true);
  };

  // Delete API call
  const handleDelete = async () => {
    if (!certificateToDelete) return;
    try {
      const res = await deleteMedicalCertificate(certificateToDelete._id);
      if (res?.success) {
        toastify(res.message || "Certificate deleted", "success");
        fetchCertificates();
      } else {
        toastify(res?.message || "Failed to delete certificate", "error");
      }
    } catch (err) {
      console.error(err);
      toastify("Error deleting certificate", "error");
    } finally {
      setDeleteModalOpen(false);
      setCertificateToDelete(null);
    }
  };

  const clearForm = () => {
    setIssueDate(dayjs());
    setFromDate(dayjs());
    setToDate(dayjs());
    setMcType("medical");
    setReferenceNo("");
    setDescription("");
    setAccompaniedBy("");
    if (sigRef.current) sigRef.current.clear();
  };

  // ======== TABLE COLUMNS ======== //
  const columns = [
    {
      name: "Issue Date",
      selector: (row) => new Date(row.issueDate).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "From",
      selector: (row) => new Date(row.fromDate).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "To",
      selector: (row) => new Date(row.toDate).toLocaleDateString(),
      sortable: true,
    },
    { name: "Type", selector: (row) => row.mcType, sortable: true },
    { name: "Doctor", selector: (row) => row.doctor, sortable: true },
    { name: "Reference No", selector: (row) => row.referenceNo || "-" },
    {
      name: "Action",
      cell: (row) => (
        <div
          style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}
        >
          {isDoctor && (
            <>
              <div
                className="cursor-pointer"
                onClick={() => handleEdit(row)}
                title="Edit"
              >
                <i className="fa fa-pen text-primary"></i>
              </div>
              <div
                className="cursor-pointer"
                onClick={() => confirmDelete(row)}
                title="Delete"
              >
                <i className="fa fa-trash text-danger"></i>
              </div>
            </>
          )}

          <div
            className="cursor-pointer"
            onClick={() => handlePrint(row)}
            title="Print"
          >
            <i className="fa fa-print text-success"></i>
          </div>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const handlePrint = (row) => {
    setSelectedCertificate(row);
    setPdfModal(true);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };

  return (
    <div style={{ padding: "50px", paddingTop: "40px", paddingBottom: "30px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Medical Certificate</h2>
        {isDoctor && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
          >
            + Create Medical Certificate
          </Button>
        )}
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data}
        pagination
        highlightOnHover
        noDataComponent="No medical certificates found"
      />

      {/* Create Certificate Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create Medical Certificate</DialogTitle>
        <DialogContent>
          <div style={{ padding: "20px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="font-400-16 mb-1">Issue Date</div>
              <DateInput
                value={issueDate}
                placeholder="Issue Date"
                onChange={(e) => {
                  const newDate = dayjs(e);

                  setIssueDate(newDate);
                }}
              />

              <FormLabel style={{ marginTop: 15 }}>MC Type</FormLabel>
              <RadioGroup
                row
                value={mcType}
                onChange={(e) => setMcType(e.target.value)}
              >
                <FormControlLabel
                  value="medical"
                  control={<Radio />}
                  label="Medical Certificate"
                  sx={{ color: "black" }}
                />
                <FormControlLabel
                  value="attendance"
                  control={<Radio />}
                  label="Certificate of Attendance"
                  sx={{ color: "black" }}
                />
              </RadioGroup>

              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="font-400-16 mb-1">From Date</div>
                <DateInput
                  value={fromDate}
                  placeholder="Start Date"
                  onChange={(e) => {
                    const newDate = dayjs(e); // convert Date -> Dayjs

                    setFromDate(newDate);
                  }}
                />
                <div className="font-400-16 mb-1">To Date</div>
                <DateInput
                  value={toDate}
                  placeholder="End Date"
                  onChange={(e) => {
                    const newDate = dayjs(e);
                    if (newDate.isBefore(fromDate)) {
                      toastify("End date can't be before start date", "error");
                      return;
                    }
                    setToDate(newDate);
                  }}
                />
              </div>

              <TextField
                label="Doctor"
                fullWidth
                margin="dense"
                value={doctor}
                onChange={(e) => setDoctor(e.target.value)}
                required
              />

              <TextField
                label="Reference No"
                fullWidth
                margin="dense"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
              />

              <TextField
                label="Description"
                fullWidth
                margin="dense"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />

              {mcType === "attendance" && (
                <TextField
                  label="Accompanied By"
                  fullWidth
                  margin="dense"
                  value={accompaniedBy}
                  onChange={(e) => setAccompaniedBy(e.target.value)}
                />
              )}
            </LocalizationProvider>

            {/* <div style={{ marginTop: "20px" }}>
              <FormLabel>Doctor Signature</FormLabel>
              <SignatureCanvas
                ref={sigRef}
                penColor="green"
                canvasProps={{
                  style: {
                    width: "100%",
                    height: "200px",
                    backgroundColor: "#f8f8f8",
                  },
                }}
              />
              <Button onClick={() => sigRef.current.clear()} size="small">
                Clear
              </Button>
            </div> */}

            <div style={{ marginTop: "20px", width: "25%" }}>
              <FormLabel>Doctor Signature</FormLabel>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <SignatureCanvas
                  ref={sigRef}
                  penColor="green"
                  canvasProps={{
                    style: {
                      width: "200px", // fixed width
                      height: "200px", // fixed height
                      backgroundColor: "#f8f8f8",
                    },
                  }}
                />
              </div>
              <Button onClick={() => sigRef.current.clear()} size="small">
                Clear
              </Button>
            </div>

            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <Button
                onClick={() => setOpen(false)}
                style={{ marginRight: 10 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      <Modal
        open={pdfModal}
        onClose={() => setPdfModal(false)}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ width: "80%", height: "90%", background: "white" }}>
          <PDFViewer style={{ width: "100%", height: "100%" }}>
            <MedicalCertificatePDF
              certificate={selectedCertificate}
              setting={setting}
            />
          </PDFViewer>
        </div>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <Box sx={style}>
          <Typography id="confirm-delete-title" variant="h6" component="h2">
            Confirm Delete
          </Typography>
          <Typography id="confirm-delete-description">
            Are you sure you want to delete this certificate?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(selectedCertificate)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default MedicalCertificate;
